import { Dropdown, Menu, Space } from 'antd';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MenuOutlined } from '@ant-design/icons';

import './ProfilePage.scss';
import Term from './components/term/Term';
import Privacy from './components/privacy/Privacy';
import { TRootState } from 'src/stores';
import StatusBox from 'src/components/status-box';
import { BaseText } from 'src/components/typography';
import { TTabItem } from 'src/interfaces/common-interface';
import { PaymentMethod, MySubcription } from './components';
import { EUserType } from 'src/variables/enum-variables';
import GeneralInformation from './components/general-information/GeneralInformation';
import ClinicInformation from './components/clinic-information/ClinicInformation';
import { CommonContent, Container } from 'src/components/containers';
import {
  EProfileTabKey,
  OWNER_PROFILE_TAB_LABEL,
  PRACTITIONER_LABEL,
  SOLO_PROFILE_TAB_LABEL,
} from './profile-page-constants';
import Notification from './components/notification/Notification';
import ContactHelp from './components/contact-help/ContactHelp';

const ProfilePage = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const profile = useSelector((state: TRootState) => state.user.profile);
  const [activeTabKey, setActiveTabKey] = useState<EProfileTabKey>();
  
  // Choose the correct set of tabs based on user role
  let profileTabLabel = PRACTITIONER_LABEL;
  if (profile?.role === EUserType.OWNER) {
    profileTabLabel = OWNER_PROFILE_TAB_LABEL;
  } else if (profile?.role === EUserType.SOLO_PRACTITIONER) {
    profileTabLabel = SOLO_PROFILE_TAB_LABEL;
  }

  useEffect(() => {
    if (searchParams && searchParams.get('tab') && searchParams.get('tab') === EProfileTabKey.SUBSCRIPTION) {
      setActiveTabKey(EProfileTabKey.SUBSCRIPTION);
    }
  }, []);

  // Fix TypeScript issue with the proper type for item
  const getTabContent = (key: EProfileTabKey) => {
    switch (key) {
      case EProfileTabKey.GENERAL_INFOMATION:
        return <GeneralInformation />;
      case EProfileTabKey.CLINIC_INFORMATION:
        return <ClinicInformation />;
      case EProfileTabKey.PAYMENT_METHOD:
        return <PaymentMethod />;
      case EProfileTabKey.SUBSCRIPTION:
        return <MySubcription />;
      case EProfileTabKey.NOTIFICATION:
        return <Notification />;
      case EProfileTabKey.CONTACT_HELP:
        return <ContactHelp />;
      case EProfileTabKey.TERM:
        return <Term />;
      case EProfileTabKey.PRIVACY:
        return <Privacy />;
      default:
        return null;
    }
  };

  // Create menu items for the dropdown on mobile
  const menuItems = Object.keys(profileTabLabel).map((key) => ({
    key,
    label: profileTabLabel[key as keyof typeof profileTabLabel],
  }));

  const handleChangeTab = (tabKey: string) => {
    setActiveTabKey(tabKey as EProfileTabKey);
  };

  // Current active tab content
  const currentTabContent = getTabContent(
    activeTabKey || (state?.activeTab as EProfileTabKey) || EProfileTabKey.GENERAL_INFOMATION
  );
  
  // Mobile dropdown menu handler
  const handleMenuClick = (e: { key: string }) => {
    handleChangeTab(e.key);
  };

  // Current active tab label for mobile display
  const currentTabLabel = profileTabLabel[
    (activeTabKey || (state?.activeTab as EProfileTabKey) || EProfileTabKey.GENERAL_INFOMATION) as keyof typeof profileTabLabel
  ];

  return (
    <Container className="ProfilePage">
      <CommonContent
        title={
          <Space size={16} className="ProfilePage__title">
            <BaseText type="display1">My account</BaseText>
            <StatusBox status={state?.client?.status} isShowDischarge />
          </Space>
        }
        className="ProfilePage__wrapper"
      >
        <div className="ProfilePage__content">
          {/* Mobile dropdown menu */}
          <div className="ProfilePage__mobile-tabs">
            <Dropdown
              overlay={<Menu onClick={handleMenuClick} items={menuItems} />}
              trigger={['click']}
            >
              <div className="ProfilePage__dropdown-trigger">
                <BaseText type="heading">{currentTabLabel}</BaseText>
                <MenuOutlined />
              </div>
            </Dropdown>
          </div>
          
          {/* Desktop sidebar menu */}
          <div className="ProfilePage__sidebar">
            <Menu
              className="ProfilePage__menu"
              selectedKeys={[activeTabKey || (state?.activeTab as string) || EProfileTabKey.GENERAL_INFOMATION]}
              mode="vertical"
              onClick={handleMenuClick}
              items={menuItems}
            />
          </div>
          
          {/* Tab content area */}
          <div className="ProfilePage__content-area">
            {currentTabContent}
          </div>
        </div>
      </CommonContent>
    </Container>
  );
};

export default ProfilePage;
